<template>
    <div
        class="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
    >
        <div
            class="flex items-center justify-between border-b border-gray-100 px-5 py-3 dark:border-gray-700/50"
        >
            <h3 class="text-sm font-medium text-gray-900 dark:text-white">Chave global</h3>
            <button
                type="button"
                class="rounded-md bg-gray-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-gray-800 disabled:opacity-50 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-200"
                :disabled="loading"
                @click="modalOpen = true"
            >
                Mudar posição
            </button>
        </div>

        <dl class="divide-y divide-gray-100 dark:divide-gray-700/50">
            <div class="grid grid-cols-3 gap-4 px-5 py-3">
                <dt class="text-xs text-gray-500 dark:text-gray-400">Posição atual</dt>
                <dd class="col-span-2 text-sm font-medium text-gray-900 dark:text-white">
                    <span v-if="loading">Carregando...</span>
                    <span v-else>{{ currentLabel }}</span>
                </dd>
            </div>
            <div class="grid grid-cols-3 gap-4 px-5 py-3">
                <dt class="text-xs text-gray-500 dark:text-gray-400">Última mudança</dt>
                <dd class="col-span-2 text-sm text-gray-900 dark:text-white">
                    <template v-if="!loading && switchState.updated_by">
                        {{ switchState.updated_by }}
                        <span class="text-gray-500 dark:text-gray-400">
                            em {{ switchState.updated_at }}
                        </span>
                    </template>
                    <span v-else-if="!loading" class="text-gray-500 dark:text-gray-400">
                        Nenhuma mudança registrada.
                    </span>
                </dd>
            </div>
            <div class="grid grid-cols-3 gap-4 px-5 py-3">
                <dt class="text-xs text-gray-500 dark:text-gray-400">Motivo</dt>
                <dd class="col-span-2 text-sm text-gray-900 dark:text-white">
                    {{ switchState.reason || "—" }}
                </dd>
            </div>
            <div class="grid grid-cols-3 gap-4 px-5 py-3">
                <dt class="text-xs text-gray-500 dark:text-gray-400">Marcações hoje</dt>
                <dd class="col-span-2 text-sm text-gray-900 dark:text-white">
                    {{ markingsToday }}
                </dd>
            </div>
        </dl>

        <experience-change-modal
            :open="modalOpen"
            title="Mudar a posição da chave global"
            description="A chave alcança a base inteira. Confira o alcance antes de confirmar."
            :options="options"
            :current-value="switchState.value"
            :submitting="submitting"
            @close="modalOpen = false"
            @confirm="submit"
        >
            <template #scope="{ value }">
                <p class="font-medium">Alcance da mudança</p>
                <p class="mt-1">{{ afterChange(value) }}</p>
                <p class="mt-1">{{ effectFor(value) }}</p>
                <p class="mt-1">{{ retentionNote }}</p>
            </template>
        </experience-change-modal>
    </div>
</template>

<script>
import ExperienceChangeModal from "./ExperienceChangeModal.vue";

// Vocabulario do PRD (User Experience -> Telas do Smaug).
const SWITCH_LABELS = {
    off: "Desligado",
    selective: "Seletivo",
    all: "Todos",
};

const SWITCH_EFFECTS = {
    off: "Todas as ofertas passam a servir o Legado, inclusive as de produtores e ofertas marcados em Checkout 2.0. As marcações continuam gravadas e voltam a valer quando a chave sair de Desligado.",
    selective:
        "Ofertas sem marcação seguem no Legado. Só os produtores e as ofertas marcados em Checkout 2.0 são liberados.",
    all: "Ofertas sem marcação passam a ser liberadas para o Checkout 2.0. Produtores e ofertas marcados em Legado continuam no Legado.",
};

const plural = (count, singular, pluralForm) => count + " " + (count === 1 ? singular : pluralForm);

const producers = (count) => plural(count, "produtor", "produtores");

const offers = (count) => plural(count, "oferta", "ofertas");

const lowerFirst = (text) => text.charAt(0).toLowerCase() + text.slice(1);

// Junta só as partes com contagem, para não listar "0 em Legado".
const joinParts = (parts) =>
    parts.length > 1 ? parts.slice(0, -1).join(", ") + " e " + parts[parts.length - 1] : parts[0];

// US-011 AC-5: a retenção não depende da posição da chave, então o alcance sempre a cita.
const RETENTION_NOTE =
    "Ofertas com domínio próprio, e os funis delas, seguem no Legado em qualquer posição da chave, até o card 6.4.";

export default {
    name: "ExperienceSwitchPanel",

    components: { ExperienceChangeModal },

    emits: ["changed"],

    data() {
        return {
            loading: true,
            submitting: false,
            modalOpen: false,
            retentionNote: RETENTION_NOTE,
            switchState: {
                value: "off",
                is_default: true,
                reason: null,
                updated_by: null,
                updated_at: null,
                counts: { producer: { legacy: 0, v2: 0 }, offer: { legacy: 0, v2: 0 } },
            },
        };
    },

    computed: {
        currentLabel() {
            const label = SWITCH_LABELS[this.switchState.value] || this.switchState.value;

            return this.switchState.is_default ? label + " (padrão)" : label;
        },

        options() {
            return Object.keys(SWITCH_LABELS).map((value) => ({
                value,
                label: SWITCH_LABELS[value],
                help: SWITCH_EFFECTS[value],
            }));
        },

        markingsToday() {
            return this.markingsFor(this.switchState.value);
        },
    },

    mounted() {
        this.fetch();
    },

    methods: {
        fetch() {
            this.loading = true;

            return axios
                .get("/api/checkout-experience/switch")
                .then(({ data }) => {
                    this.switchState = data;
                })
                .catch(({ response }) => {
                    this.$notify(
                        {
                            group: "error",
                            title: "Erro",
                            text: this.errorText(response, "Falha ao carregar a chave global."),
                        },
                        4000,
                    );
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        // Quem não está no Checkout 2.0 está no Legado: o texto parte da posição da chave
        // e só cita as marcações que mudam alguém de lado.
        markingsFor(position) {
            const counts = this.switchState.counts || {};
            const producer = counts.producer || { legacy: 0, v2: 0 };
            const offer = counts.offer || { legacy: 0, v2: 0 };

            const offerParts = [];

            if (offer.v2) {
                offerParts.push(offers(offer.v2) + " no Checkout 2.0");
            }

            if (offer.legacy) {
                offerParts.push(offers(offer.legacy) + " no Legado");
            }

            const exceptions = offerParts.length
                ? "Exceções por oferta: " + joinParts(offerParts) + "."
                : "Nenhuma oferta com exceção.";

            if (position === "all") {
                const rest = producer.legacy
                    ? "Todos os produtores no Checkout 2.0, exceto " + producers(producer.legacy) + " no Legado."
                    : "Todos os produtores no Checkout 2.0.";

                return rest + " " + exceptions;
            }

            if (position === "selective") {
                const rest = producer.v2
                    ? producers(producer.v2) + " no Checkout 2.0; os demais seguem no Legado."
                    : "Nenhum produtor no Checkout 2.0; todos seguem no Legado.";

                return rest + " " + exceptions;
            }

            const saved = [];

            if (producer.v2) {
                saved.push(producers(producer.v2) + " no Checkout 2.0");
            }

            if (producer.legacy) {
                saved.push(producers(producer.legacy) + " no Legado");
            }

            saved.push(...offerParts.map((part) => part + " por exceção"));

            const off = "Todos no Legado, porque a chave está em Desligado.";

            return saved.length ? off + " Marcações guardadas para quando ela sair: " + joinParts(saved) + "." : off;
        },

        // Na confirmação, o mesmo texto na posição escolhida: como fica depois de confirmar.
        // O modal abre na posição atual, que não muda nada.
        afterChange(value) {
            const prefix = value === this.switchState.value ? "Sem mudança: " : "Depois da mudança: ";

            return prefix + lowerFirst(this.markingsFor(value));
        },

        effectFor(value) {
            return SWITCH_EFFECTS[value] || "";
        },

        submit({ value, reason }) {
            if (this.submitting) {
                return;
            }

            this.submitting = true;

            axios
                .put("/api/checkout-experience/switch", { value, reason })
                .then(({ data }) => {
                    this.modalOpen = false;

                    this.$notify(
                        {
                            group: data.changed ? "success" : "error",
                            title: data.changed ? "Chave global atualizada" : "Nada mudou",
                            text: data.changed
                                ? "A nova posição vale para o comprador em até 60 segundos."
                                : "A chave global já estava nessa posição. Nada foi registrado.",
                        },
                        4000,
                    );

                    return this.fetch().then(() => {
                        this.$emit("changed");
                    });
                })
                .catch(({ response }) => {
                    this.$notify(
                        {
                            group: "error",
                            title: "Erro",
                            text: this.errorText(response, "Falha ao mudar a chave global."),
                        },
                        6000,
                    );
                })
                .finally(() => {
                    this.submitting = false;
                });
        },

        // O Handler do Smaug devolve {errors: [...]} para validação e {message} nos demais casos.
        errorText(response, fallback) {
            const data = response?.data;

            if (Array.isArray(data?.errors) && data.errors.length) {
                return data.errors.join(" ");
            }

            return data?.message || fallback;
        },
    },
};
</script>
